import React, { useEffect, useMemo, useState } from 'react';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { RestaurantProvider, useRestaurant } from './context/RestaurantContext';
import { Header } from './components/Header';
import { CategoryNav } from './components/CategoryNav';
import { CategoryGrid } from './components/CategoryGrid';
import { MenuSection } from './components/MenuSection';
import { BowlBuilder } from './components/BowlBuilder';
import { ProductCustomizer } from './components/ProductCustomizer';
import { Cart } from './components/Cart';
import { Checkout } from './components/Checkout';
import { CartBadge } from './components/CartBadge';
import { Product, CartItemType } from './types/types';
import { useCart } from './context/CartContext';
import { AnimatePresence } from 'framer-motion';
import { RestaurantDashboard } from './admin/RestaurantDashboard';
import { QRGenerator } from './admin/QRGenerator';
import { TableOrderCheck } from './components/TableOrderCheck';
import { supabase } from './lib/supabase';
import { KitchenClosedBanner } from './components/KitchenClosedBanner';
import { ClosedHoursBanner } from './components/ClosedHoursBanner';
import { OrderTracking } from './components/OrderTracking';
import { OrderLookup } from './components/OrderLookup';
import { SupabaseProduct } from './types/restaurant';
type ViewState = 'menu' | 'bowl-builder' | 'product-customizer' | 'checkout' | 'restaurant-dashboard' | 'order-tracking' | 'qr-generator' | 'order-lookup';
/** Convert a SupabaseProduct to the local Product type used by components */
function toProduct(sp: SupabaseProduct): Product {
  return {
    id: sp.id,
    name: sp.name,
    description: sp.description || undefined,
    price: sp.price,
    category: sp.category,
    subcategory: sp.subcategory || undefined,
    image: sp.image || undefined,
    customizable: sp.customizable,
    type: sp.type as Product['type'] || undefined
  };
}
function AppContent() {
  const {
    restaurant,
    restaurantId,
    products,
    categories,
    cleanHost
  } = useRestaurant();
  const [trackingToken, setTrackingToken] = useState<string | null>(null);
  const [view, setView] = useState<ViewState>(() => {
    if (window.location.hash === '#admin' || window.location.hash === '#caja' || window.location.hash === '#cocina' || window.location.hash === '#pizzeria' || window.location.hash === '#bebidas') return 'restaurant-dashboard';
    if (window.location.hash === '#qr') return 'qr-generator';
    const path = window.location.pathname;
    if (path.startsWith('/track/')) {
      const token = path.split('/track/')[1];
      if (token) return 'order-tracking';
    }
    return 'menu';
  });
  useEffect(() => {
    if (view === 'order-tracking') {
      const token = window.location.pathname.split('/track/')[1];
      if (token) setTrackingToken(token);
    }
  }, []);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingItem, setEditingItem] = useState<CartItemType | null>(null);
  const [kitchenPaused, setKitchenPaused] = useState(restaurant?.kitchen_paused || false);
  const [isOutsideHours, setIsOutsideHours] = useState(false);
  const [tableOrderCheck, setTableOrderCheck] = useState<{
    show: boolean;
    tableNumber: string;
    orderNumber: string;
    trackingToken: string;
  } | null>(null);
  const {
    addItem
  } = useCart();
  // Convert Supabase products to local Product type
  const menuProducts = useMemo(() => products.map(toProduct), [products]);
  // Check for existing open orders on this table — filtered by restaurant_id
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const table = params.get('table');
    if (!table || view !== 'menu' || !restaurantId) return;
    const checkTableOrders = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('orders').select('order_number, tracking_token, status').eq('restaurant_id', restaurantId).eq('table_number', table).in('status', ['new', 'accepted', 'preparing', 'ready']).order('created_at', {
          ascending: false
        }).limit(1);
        if (error) {
          console.error('Error checking table orders:', error);
          return;
        }
        if (data && data.length > 0) {
          setTableOrderCheck({
            show: true,
            tableNumber: table,
            orderNumber: data[0].order_number,
            trackingToken: data[0].tracking_token
          });
        }
      } catch (err) {
        console.error('Error checking table:', err);
      }
    };
    checkTableOrders();
  }, [restaurantId]);
  // Check operating hours from restaurant profile
  useEffect(() => {
    if (!restaurant) return;
    const checkHours = () => {
      const now = new Date();
      const hour = now.getHours();
      const outside = hour < restaurant.open_hour || hour >= restaurant.close_hour;
      setIsOutsideHours(outside);
    };
    checkHours();
    const interval = setInterval(checkHours, 60000);
    return () => clearInterval(interval);
  }, [restaurant]);
  // Sync kitchen_paused from restaurant profile
  useEffect(() => {
    if (restaurant) {
      setKitchenPaused(restaurant.kitchen_paused);
    }
  }, [restaurant]);
  // Check kitchen status periodically — filtered by restaurant_id
  useEffect(() => {
    if (view === 'restaurant-dashboard' || !restaurantId) return;
    const checkKitchenStatus = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('restaurant_id', restaurantId).single();
        if (error) {
          console.error('Error fetching kitchen status:', error);
          return;
        }
        if (data) {
          setKitchenPaused(data.kitchen_paused);
          if (data.kitchen_paused && view !== 'menu') {
            setView('menu');
          }
          if (restaurant) {
            const hour = new Date().getHours();
            const outside = hour < restaurant.open_hour || hour >= restaurant.close_hour;
            if (outside && view !== 'menu') {
              setView('menu');
            }
          }
        }
      } catch (err) {
        console.error('Error checking kitchen status:', err);
      }
    };
    checkKitchenStatus();
    const interval = setInterval(checkKitchenStatus, 30000);
    return () => clearInterval(interval);
  }, [view, restaurantId, restaurant]);
  const filteredProducts = useMemo(() => {
    if (activeCategory === 'Todos') return menuProducts;
    return menuProducts.filter(p => p.category === activeCategory);
  }, [activeCategory, menuProducts]);
  const groupedProducts = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    filteredProducts.forEach(p => {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    });
    return groups;
  }, [filteredProducts]);
  const handleAddProduct = (product: Product) => {
    if (product.customizable) {
      setSelectedProduct(product);
      setEditingItem(null);
      setView('product-customizer');
    } else {
      addItem({
        id: `${product.id}_${Date.now()}`,
        productId: product.id,
        name: product.name,
        basePrice: product.price,
        quantity: 1,
        selections: {},
        subtotal: product.price,
        type: product.type || 'bebida'
      });
    }
  };
  const handleEditItem = (item: CartItemType) => {
    setEditingItem(item);
    if (item.type === 'bowl') {
      setView('bowl-builder');
    } else {
      const product = menuProducts.find(p => p.id === item.productId);
      if (product) {
        setSelectedProduct(product);
        setView('product-customizer');
      }
    }
  };
  return <div className="min-h-screen bg-carbon w-full flex justify-center" data-id="element-577">
      {view === 'restaurant-dashboard' && <RestaurantDashboard onBack={() => {
      window.location.hash = '';
      setView('menu');
    }} data-id="element-578" />}

      {view === 'qr-generator' && <QRGenerator onBack={() => {
      window.location.hash = '';
      setView('menu');
    }} data-id="element-579" />}

      {view === 'order-tracking' && trackingToken && <OrderTracking token={trackingToken} data-id="element-580" />}

      {view !== 'restaurant-dashboard' && view !== 'order-tracking' && view !== 'qr-generator' && <div className="w-full max-w-md bg-carbon min-h-screen relative shadow-2xl overflow-x-hidden" data-id="element-581">
            {view === 'menu' && <>
                <Header onStartOrder={() => {
          setEditingItem(null);
          setView('bowl-builder');
        }} onLookupOrder={() => setView('order-lookup')} data-id="element-582" />

                <div className="px-4 pb-24" data-id="element-583">
                  <CategoryNav activeCategory={activeCategory} onSelectCategory={setActiveCategory} data-id="element-584" />

                  {activeCategory === 'Todos' ? <CategoryGrid onSelectCategory={setActiveCategory} data-id="element-585" /> : Object.entries(groupedProducts).map(([category, prods]) => <MenuSection key={category} title={category} products={prods} onAddProduct={handleAddProduct} data-id="element-586" />)}
                </div>

                {!kitchenPaused && !isOutsideHours && <>
                    <CartBadge data-id="element-587" />
                    <Cart onCheckout={() => setView('checkout')} onEditItem={handleEditItem} data-id="element-588" />
                  </>}

                {kitchenPaused && <KitchenClosedBanner data-id="element-589" />}
                {!kitchenPaused && isOutsideHours && <ClosedHoursBanner data-id="element-590" />}

                {tableOrderCheck?.show && <TableOrderCheck tableNumber={tableOrderCheck.tableNumber} orderNumber={tableOrderCheck.orderNumber} onAddMore={() => setTableOrderCheck(null)} onViewOrder={() => {
          setTableOrderCheck(null);
          setTrackingToken(tableOrderCheck.trackingToken);
          setView('order-tracking');
          window.history.pushState({}, '', `/track/${tableOrderCheck.trackingToken}`);
        }} onClose={() => setTableOrderCheck(null)} data-id="element-591" />}
              </>}

            <AnimatePresence data-id="element-592">
              {view === 'bowl-builder' && <BowlBuilder initialItem={editingItem || undefined} onClose={() => setView('menu')} onSave={item => {
          addItem(item);
          setView('menu');
        }} data-id="element-593" />}

              {view === 'product-customizer' && selectedProduct && <ProductCustomizer product={selectedProduct} initialItem={editingItem || undefined} onClose={() => setView('menu')} onSave={item => {
          addItem(item);
          setView('menu');
        }} data-id="element-594" />}

              {view === 'checkout' && <Checkout onBack={() => setView('menu')} data-id="element-595" />}

              {view === 'order-lookup' && <OrderLookup onBack={() => setView('menu')} onTrackOrder={token => {
          setTrackingToken(token);
          setView('order-tracking');
          window.history.pushState({}, '', `/track/${token}`);
        }} data-id="element-596" />}
            </AnimatePresence>
          </div>}
    </div>;
}
/** Loading screen shown while restaurant is being resolved */
function LoadingScreen() {
  return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-4" data-id="element-597">
      <div className="w-10 h-10 border-4 border-teal/30 border-t-teal rounded-full animate-spin mb-6" data-id="element-598" />
      <p className="text-gray-400 font-medium text-sm" data-id="element-599">Cargando...</p>
    </div>;
}
/** Error screen shown when restaurant cannot be resolved */
function ErrorScreen({
  message
}: {
  message: string;
}) {
  return <div className="min-h-screen bg-carbon flex flex-col items-center justify-center p-8 text-center" data-id="element-600">
      <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6 border border-red-500/20" data-id="element-601">
        <span className="text-3xl" data-id="element-602">⚠️</span>
      </div>
      <h1 className="font-heading text-2xl text-white mb-3" data-id="element-603">
        Restaurant not configured for this domain
      </h1>
      <p className="text-gray-400 text-sm max-w-md leading-relaxed" data-id="element-604">
        {message}
      </p>
      <p className="text-gray-600 text-xs mt-6" data-id="element-605">
        Domain: {window.location.hostname}
      </p>
    </div>;
}
/** Gate component that waits for restaurant resolution */
function RestaurantGate() {
  const {
    loading,
    error
  } = useRestaurant();
  if (loading) return <LoadingScreen data-id="element-606" />;
  if (error) return <ErrorScreen message={error} data-id="element-607" />;
  return <AppContent data-id="element-608" />;
}
export function App() {
  return <RestaurantProvider data-id="element-609">
      <LanguageProvider data-id="element-610">
        <CartProvider data-id="element-611">
          <RestaurantGate data-id="element-612" />
        </CartProvider>
      </LanguageProvider>
    </RestaurantProvider>;
}